import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { CalendarDays, Camera, Check, ChevronLeft, ChevronRight, CircleX, Hash, TriangleAlert, KeyRound, LifeBuoy, Lock, MapPin, MapPinned, Pause, Phone, StickyNote, Truck, User, X } from 'lucide-react';
import { JOB_STEP, SLOT_LABEL, formatDay, formatDateTime, jobStatusLabel, todayUk } from '@nu/shared';
import { api } from '../../api/client.js';
import { queuePhotos, removePhoto, shrink } from '../../offline/photos.js';
import { outbox } from '../../offline/index.js';
import AbortJob from './AbortJob.jsx';
import PauseJob from './PauseJob.jsx';
import ReportIncident from './ReportIncident.jsx';
import { useJobIncidents } from './incidents.js';
import PhotoButtons from '../../components/PhotoButtons.jsx';
import Directions from './Directions.jsx';
import SwitchJob from './SwitchJob.jsx';
import { checkSite, completeJob, dayView, markJob, resumeJob, useLocalJobs, withProgress } from './progress.js';
import { useJobPhotos } from './useJobPhotos.js';
import { useJobSupport } from './support.js';
import RequestSupport from './RequestSupport.jsx';
import { useSchedule } from './useSchedule.js';
import VanPrompt from './VanPrompt.jsx';
import { useMyStock } from '../stock/store.js';
import ItemGroups from '../stock/ItemGroups.jsx';
import { useDraft } from '../../offline/draft.js';

// One job (app), all on one card: the job at the top (reference, address,
// status), then its steps in order. Each step opens once the one before is
// done:
//   1 Get there        On route (or Directions, which says so), then Arrived
//   2 Risk assessment  photos of the Network Plus SF344 paper form, both pages
//   3 The job          a photo before the works start, and one when they're done
// Report an incident and Request support (their supervisor, a waste tipper)
// sit above the steps from On route until the job is finished.
//   4 Close off        stock used (a photo of each meter fitted) and job notes
// Pause job (part way through, back later) keeps everything; when they're
// back on site they say whether it's as they left it, and if not the risk
// assessment is done again.
// Only the team's current job can be moved on; any other job offers Do this
// job next (with a reason). Can't install (abort, or come back later today)
// is there once they've arrived. The copy opened last is kept on the phone
// for when there's no signal; taps and photos are queued and sent when there is.

const KEY = (id) => `nu.job.${id}`;
const time = (v) => (v ? new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit' }).format(new Date(v)) : '');
// The Network Plus SF344 paper form: page 1 (start-work checks, hazards,
// signatures) and page 2 (RAMS and COSHH ticks).
const RA_SIDES = [
  { kind: 'ra_front', label: 'Page 1', hint: 'Checks and hazards' },
  { kind: 'ra_back', label: 'Page 2', hint: 'RAMS and COSHH' },
];
// The works: one photo before they start, one when they're finished.
const WORKS_SIDES = [
  { kind: 'before', label: 'Before', hint: 'Before you start' },
  { kind: 'after', label: 'After', hint: 'When it\u2019s finished' },
];

export default function ScheduleJob() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const back = params.get('day') ? `/?day=${params.get('day')}` : '/';
  const local = useLocalJobs();
  const [serverJob, setJob] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY(id)) || 'null'); } catch { return null; }
  });
  const [error, setError] = useState(null);
  const job = withProgress(serverJob, local);
  // The team's day, to know whether this is the job they're on now.
  const { data: dayData } = useSchedule(serverJob?.bookingDate || todayUk());
  const { jobs: dayJobs, current } = dayView(dayData, local);
  const listed = job ? dayJobs.find((j) => j.id === job.id) : null;
  const isCurrent = Boolean(job && current?.id === job.id);

  // Fetched on opening and refreshed while open (back to the front, every
  // 10 s, after this phone's taps), so the other engineer's changes show.
  useEffect(() => {
    let live = true;
    const load = () => api(`/my/jobs/${id}`).then(
      (j) => {
        if (!live) return;
        setJob(j);
        setError(null);
        try { localStorage.setItem(KEY(id), JSON.stringify(j)); } catch { /* fine */ }
      },
      (e) => { if (live) setError(e); },
    );
    load();
    const visible = () => document.visibilityState === 'visible';
    // Every 10 s: the team is often both on this job at once.
    const tick = setInterval(() => { if (visible() && navigator.onLine) load(); }, 10_000);
    const onShow = () => { if (visible()) load(); };
    document.addEventListener('visibilitychange', onShow);
    window.addEventListener('online', onShow);
    const off = outbox.subscribe(load);
    return () => { live = false; clearInterval(tick); document.removeEventListener('visibilitychange', onShow); window.removeEventListener('online', onShow); off(); };
  }, [id]);

  if (!job) {
    return (
      <div className="stack">
        <section className="card">
          <p><Link to={back} className="back-link"><ChevronLeft size={18} aria-hidden="true" /> Schedule</Link></p>
          <p className="muted">{error ? (error.status === 404 ? 'This job isn’t on your team’s schedule.' : 'Couldn’t load the job. Check your signal.') : 'Loading…'}</p>
        </section>
      </div>
    );
  }
  return <JobCard job={listed ? { ...job, seq: listed.seq } : job} back={back} offline={Boolean(error)} current={current} isCurrent={isCurrent} prompt={dayData?.prompt?.bookingId === job.id ? dayData.prompt : null} />;
}

function JobCard({ job, back, offline, current, isCurrent, prompt = null }) {
  const step = JOB_STEP[job.status];
  const { latest } = useJobPhotos(job.id);
  const [aborting, setAborting] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [asking, setAsking] = useState(false);
  const support = useJobSupport(job.id);
  const incidents = useJobIncidents(job.id);
  // The site changed during a pause: only risk assessment photos from after that count.
  const raPhoto = (kind) => {
    const p = latest[kind];
    return p && (!job.raResetAt || Date.parse(p.takenAt) > Date.parse(job.raResetAt)) ? p : null;
  };
  const raDone = RA_SIDES.every((s) => raPhoto(s.kind));
  // Back after a pause, not yet said whether the site is as they left it.
  const needsCheck = step === 2 && Boolean(job.pausedAt) && (!job.siteCheckedAt || Date.parse(job.siteCheckedAt) < Date.parse(job.pausedAt));
  const worksDone = WORKS_SIDES.every((s) => latest[s.kind]);
  const address = [job.addressLine1, job.addressLine2, job.town, job.postcode].filter(Boolean);
  const finished = step === 3;

  // Each step: done, open (the one to do now), or locked (waiting on the one before).
  const getThere = step >= 2 ? 'done' : finished ? 'locked' : 'open';
  const risk = needsCheck ? 'open' : raDone ? 'done' : step === 2 ? 'open' : 'locked';
  const work = needsCheck ? 'locked' : raDone && worksDone ? 'done' : step === 2 && raDone ? 'open' : 'locked';

  return (
    <div className="stack">
      <VanPrompt prompt={prompt} />
      <section className="card job-card">
        <div className="job-nav">
          <Link to={back} className="btn job-back"><ChevronLeft size={18} aria-hidden="true" /> Schedule</Link>
          <span className={`status status-${step === 0 && job.status !== 'paused' ? 'not_started' : job.status}`}>{jobStatusLabel(job.status)}</span>
        </div>

        <header className="job-head">
          <p className="job-kicker">{job.seq ? `Job ${job.seq} · ` : ''}{job.type.client} · {job.type.workType}</p>
          <h1>{job.reference}</h1>
          {finished && job.statusReason && <p className="job-reason"><strong>{jobStatusLabel(job.status)}:</strong> {job.statusReason}</p>}
          {offline && <p className="notice-line notice-warn">No signal: showing the copy saved on this phone.</p>}
          <dl className="job-facts">
            <Fact icon={<MapPin size={16} aria-hidden="true" />} label="Address"><address className="job-address">{address.map((l) => <span key={l}>{l}</span>)}</address></Fact>
            <Fact icon={<CalendarDays size={16} aria-hidden="true" />} label="When">{formatDay(job.bookingDate)} · {SLOT_LABEL[job.slot] || 'No slot'}</Fact>
            {(job.customerName || job.customerPhone) && (
              <Fact icon={<User size={16} aria-hidden="true" />} label="Customer">
                {job.customerName && <span className="fact-line">{job.customerName}</span>}
                {job.customerPhone && <a className="btn with-icon fact-call" href={`tel:${job.customerPhone.replace(/[^\d+]/g, '')}`}><Phone size={16} aria-hidden="true" /> {job.customerPhone}</a>}
              </Fact>
            )}
            {job.accessNotes && <Fact icon={<KeyRound size={16} aria-hidden="true" />} label="Access" tone="warn">{job.accessNotes}</Fact>}
            {job.clientRef && <Fact icon={<Hash size={16} aria-hidden="true" />} label="Client ref">{job.clientRef}</Fact>}
            {job.notes?.length > 0 && (
              <Fact icon={<StickyNote size={16} aria-hidden="true" />} label="From the office">
                <ul className="fact-notes">
                  {job.notes.map((n) => (
                    <li key={n.id}>
                      <span>{n.body}</span>
                      <span className="small muted">{n.authorName} · {formatDateTime(n.createdAt)}</span>
                    </li>
                  ))}
                </ul>
              </Fact>
            )}
          </dl>
        </header>

        {(step === 1 || step === 2 || incidents.length > 0 || support.length > 0) && (
          <section className="job-incidents" aria-label="Incidents and support">
            {incidents.map((i) => (
              <p key={i.id} className="incident-line">
                <TriangleAlert size={16} aria-hidden="true" />
                <span><strong>{i.reference || 'Incident'}: {i.service ? `${i.type} (${i.service})` : i.type}</strong>{i.hurt ? ', someone hurt' : ''}. {formatDateTime(i.happenedAt)}{i.local ? ' · waiting to send' : ''}</span>
              </p>
            ))}
            {support.map((r) => (
              <p key={r.id} className="support-line">
                <LifeBuoy size={16} aria-hidden="true" />
                <span><strong>{r.reference ? `${r.reference}: ` : ''}{r.type}</strong> asked for at {time(r.requestedAt)}{r.local ? ' · waiting to send' : ''}{r.notes ? <span className="small muted support-notes">{r.notes}</span> : null}</span>
              </p>
            ))}
            {(step === 1 || step === 2) && (
              // One row each, always the same height (the taller one's).
              <div className="job-actions">
                <button type="button" className="incident-btn" onClick={() => setReporting(true)}>
                  <span className="incident-btn-icon" aria-hidden="true"><TriangleAlert size={20} /></span>
                  <span className="incident-btn-text">
                    <strong>Report an incident</strong>
                    <span>Strike, damage, injury or near miss. Your supervisor is told straight away.</span>
                  </span>
                  <ChevronRight size={20} aria-hidden="true" className="incident-btn-go" />
                </button>
                <button type="button" className="incident-btn support-btn" onClick={() => setAsking(true)}>
                  <span className="incident-btn-icon" aria-hidden="true"><LifeBuoy size={20} /></span>
                  <span className="incident-btn-text">
                    <strong>Request support</strong>
                    <span>Your supervisor on site, or a waste tipper. Operations are told too.</span>
                  </span>
                  <ChevronRight size={20} aria-hidden="true" className="incident-btn-go" />
                </button>
              </div>
            )}
          </section>
        )}

        <ol className="steps">
          <Step n={1} title="Get there" state={getThere} lockedText="">
            {getThere === 'done'
              ? (
                <>
                  <p className="muted">Arrived{job.startedAt ? ` at ${time(job.startedAt)}` : ''}.</p>
                  {/* On site, before anything else: if they can't install, say so here. */}
                  {step === 2 && (
                    <div className="step-row">
                      <button type="button" className="btn with-icon step-btn" onClick={() => setPausing(true)}><Pause size={20} aria-hidden="true" /> Pause job</button>
                      <button type="button" className="btn with-icon abort-btn step-btn" onClick={() => setAborting(true)}><CircleX size={20} aria-hidden="true" /> Can&apos;t install?</button>
                    </div>
                  )}
                </>
              )
              : <GetThere job={job} current={current} isCurrent={isCurrent} />}
          </Step>

          <Step n={2} title="Risk assessment" state={risk} lockedText={finished ? 'Not done.' : 'Once you have arrived.'}>
            {needsCheck ? (
              <div className="site-check">
                <p><strong>Back after the pause.</strong> Is the site still as you left it?</p>
                <div className="step-row">
                  <button type="button" className="btn primary step-btn" onClick={() => checkSite(job, true)}>Yes, carry on</button>
                  <button type="button" className="btn step-btn" onClick={() => checkSite(job, false)}>No, it&apos;s changed</button>
                </div>
                <p className="small muted">Say No if anything is different: new hazards, the dig disturbed, different people on site. You then do the risk assessment again.</p>
              </div>
            ) : (
            <>
            {job.raResetAt && !raDone && <p className="notice-line notice-warn">The site changed while the job was paused. Do the risk assessment again and photograph both pages.</p>}
            <p className="small muted">Fill in the Network Plus SF344 site risk assessment with everyone on site signing it, then photograph both pages. Both photos are needed before you start the work. If any start-work check is No, stop and call your supervisor.</p>
            <div className="ra-sides">
              {RA_SIDES.map((s) => <RaSide key={s.kind} job={job} side={s} photo={raPhoto(s.kind)} canTake={step === 2} what="Risk assessment" />)}
            </div>
            </>
            )}
          </Step>

          <Step n={3} title="The job" state={work} lockedText={step === 2 ? 'After the risk assessment photos.' : finished ? '' : 'After the risk assessment.'}>
            <p className="small muted">Photograph the site before you start, from where the whole work area shows. When the works are finished and reinstated, take the after photo from the same spot.</p>
            <div className="ra-sides">
              {WORKS_SIDES.map((s) => (
                <RaSide key={s.kind} job={job} side={s} photo={latest[s.kind]} what="Works" canTake={step === 2 && raDone && (s.kind === 'before' || Boolean(latest.before))} />
              ))}
            </div>
            {step === 2 && raDone && latest.before && !latest.after && <p className="small muted">Take the after photo once the works are finished.</p>}
          </Step>

          <Step n={4} title="Stock and close off" state={job.status === 'completed' ? 'done' : work === 'done' && step === 2 ? 'open' : 'locked'} lockedText={finished ? '' : 'After the works photos.'}>
            {job.status === 'completed'
              ? (
                <>
                  <p className="muted">Completed{job.finishedAt ? ` at ${time(job.finishedAt)}` : ''}.</p>
                  {job.jobNotes && <p className="job-notes-done">{job.jobNotes}</p>}
                </>
              )
              : <CloseOff job={job} />}
          </Step>
        </ol>
        {asking && <RequestSupport job={job} onClose={() => setAsking(false)} />}
        {pausing && <PauseJob day={job.bookingDate} job={job} onClose={() => setPausing(false)} />}
        {aborting && <AbortJob day={job.bookingDate} job={job} start={aborting.start} onClose={() => setAborting(false)} />}
        {reporting && (
          <ReportIncident
            job={job}
            onClose={() => setReporting(false)}
            // The job can't carry on: go straight to Can't install, once arrived.
            onSent={({ canContinue, type }) => { if (!canContinue && step === 2) setAborting({ start: { reason: 'Other', note: `Incident: ${type}` } }); }}
          />
        )}
      </section>
    </div>
  );
}

function Step({ n, title, state, lockedText, children }) {
  return (
    <li className={`step step-${state}`} aria-current={state === 'open' ? 'step' : undefined}>
      <div className="step-head">
        <span className="step-n" aria-hidden="true">{state === 'done' ? <Check size={16} /> : state === 'locked' ? <Lock size={14} /> : n}</span>
        <h2>{title}</h2>
        <span className="sr-only">{state === 'done' ? ', done' : state === 'locked' ? ', not yet' : ', to do now'}</span>
      </div>
      <div className="step-body">
        {state === 'locked' ? (lockedText ? <p className="small muted">{lockedText}</p> : null) : children}
      </div>
    </li>
  );
}

// Step 4: the stock used, a photo of each meter fitted, job notes, then close
// the job off. What's typed is kept on the phone, so leaving the job (or no
// signal) doesn't lose it. Which items can be logged is the job stock list
// (Nano Fibre's NU Job Stock switch); once there is one, at least one item
// has to be logged. Meters (category civil) need a photo each.
function CloseOff({ job }) {
  const key = `nu.jobNotes.${job.id}`;
  const [notes, setNotes] = useState(() => {
    try { return localStorage.getItem(key) || ''; } catch { return ''; }
  });
  const change = (e) => {
    setNotes(e.target.value);
    try { localStorage.setItem(key, e.target.value); } catch { /* lasts this visit */ }
  };
  const { data: stock } = useMyStock();
  const items = stock?.jobItems || [];
  const [draft, saveDraft, clearDraft] = useDraft(`nu.draft.jobStock.${job.id}`);
  const qty = draft.qty || {};
  const set = (id, v) => saveDraft({ qty: { ...qty, [id]: v } });
  const used = (id) => Number(qty[id]) > 0;
  const lines = items.filter((i) => used(i.id)).map((i) => ({ itemId: i.id, qty: Number(qty[i.id]) }));
  const meters = items.filter((i) => i.category === 'civil' && used(i.id)).reduce((n, i) => n + Number(qty[i.id]), 0);
  const { all } = useJobPhotos(job.id);
  const meterPhotos = all.filter((p) => p.kind === 'meter');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const takeMeter = async (files) => {
    setBusy(true);
    setError(null);
    try {
      for (const f of files) await queuePhotos(job.id, [await shrink(f, { label: job.reference })], 'meter');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  const missing = items.length > 0 && !lines.length ? 'Log the stock used (at least one item).'
    : meters > meterPhotos.length ? `Take a photo of each meter fitted (${meterPhotos.length} of ${meters}).` : null;
  const done = async () => {
    if (missing) return;
    await completeJob(job, notes, lines);
    clearDraft();
    try { localStorage.removeItem(key); } catch { /* fine */ }
  };
  return (
    <>
      {items.length > 0 && (
        <div className="job-stock">
          <h3 className="job-stock-title">Stock used</h3>
          <ItemGroups items={items} values={qty} onChange={set} done={used} verb="How many used:" tally={(g) => `${g.filter((x) => used(x.id)).length} used`} whole={(g) => g.some((x) => used(x.id))} />
        </div>
      )}
      {meters > 0 && (
        <div className="job-meters">
          <h3 className="job-stock-title">Meter photos <span className={`small ${meterPhotos.length >= meters ? 'ok' : 'bad'}`}>{meterPhotos.length} of {meters}</span></h3>
          <p className="small muted">One photo of each meter fitted, with its serial number showing.</p>
          {meterPhotos.length > 0 && (
            <div className="ra-sides">
              {meterPhotos.map((p, n) => <MeterPhoto key={p.id} job={job} photo={p} n={n + 1} />)}
            </div>
          )}
          {meterPhotos.length < meters && <PhotoButtons onFiles={takeMeter} disabled={busy} primary takeLabel={busy ? 'Adding…' : 'Take meter photo'} title="Meter fitted" />}
          {error && <span className="popup-hint">{error}</span>}
        </div>
      )}
      <label className="popup-field job-notes">
        <span>Job notes <span className="muted">(optional)</span></span>
        <textarea className="popup-input" rows={4} value={notes} onChange={change} maxLength={4000} placeholder="Anything the office should know: what was fitted, where, anything left to do" />
      </label>
      {missing && <p className="small muted">{missing}</p>}
      <button type="button" className="btn primary with-icon step-btn" disabled={Boolean(missing)} onClick={done}><Check size={20} aria-hidden="true" /> Complete job</button>
    </>
  );
}

// Step 1: the buttons that get the team there.
function GetThere({ job, current, isCurrent }) {
  const step = JOB_STEP[job.status];
  const [asking, setAsking] = useState(false);
  if (step === 0 && !isCurrent && current && JOB_STEP[current.status] === 2) {
    return <p className="muted">{job.status === 'paused' ? `Paused: ${job.statusReason || 'no reason'}.` : 'Not started.'} Job {current.seq} is in progress: finish it, pause it or tap Can&apos;t install? before {job.status === 'paused' ? 'coming back to' : 'starting'} this one.</p>;
  }
  if (step === 0 && !isCurrent) {
    return (
      <>
        <p className="muted">{job.status === 'paused' ? `Paused: ${job.statusReason || 'no reason'}.` : 'Not started.'}{current ? ` Your team is on job ${current.seq} now.` : ''}</p>
        {job.status === 'paused'
          ? <button type="button" className="btn primary with-icon step-btn" onClick={() => resumeJob(job.bookingDate, job, current)}><Truck size={20} aria-hidden="true" /> Resume job</button>
          : <button type="button" className="btn primary step-btn" onClick={() => setAsking(true)}>Do this job next</button>}
        {job.status === 'paused' && current && JOB_STEP[current.status] === 1 && <p className="small muted">Job {current.seq} goes back to Not started.</p>}
        {asking && <SwitchJob day={job.bookingDate} job={job} current={current} onView={() => setAsking(false)} onClose={() => setAsking(false)} />}
      </>
    );
  }
  if (step === 0) {
    return (
      <>
        {job.status === 'paused'
          ? <p className="small muted"><strong>Paused: {job.statusReason || 'no reason'}.</strong> Everything done so far is kept. Tap Resume job when you head back.</p>
          : <p className="small muted">Tap On route when you set off, or use Directions (it does both).</p>}
        <div className="step-row">
          <Directions job={job} className="btn with-icon step-btn" />
          <button type="button" className="btn primary with-icon step-btn" onClick={() => markJob(job, 'on_the_way')}><Truck size={20} aria-hidden="true" /> {job.status === 'paused' ? 'Resume job' : 'On route'}</button>
        </div>
      </>
    );
  }
  return (
    <>
      <p><strong>On route</strong>{job.onRouteAt ? ` since ${time(job.onRouteAt)}` : ''}.</p>
      <div className="step-row">
        <Directions job={job} className="btn with-icon step-btn" />
        <button type="button" className="btn primary with-icon step-btn" onClick={() => markJob(job, 'in_progress')}><MapPinned size={20} aria-hidden="true" /> Arrived</button>
      </div>
    </>
  );
}

// One photo the step needs (a risk assessment page, before/after works):
// take it, see it and retake, or remove it (the wrong one was taken).
function RaSide({ job, side, photo, canTake, what }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [removing, setRemoving] = useState(false);
  const remove = async () => {
    setRemoving(false);
    try { await removePhoto(outbox, job.id, photo); } catch (err) { setError(err.message); }
  };
  const take = async ([file]) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await queuePhotos(job.id, [await shrink(file, { label: job.reference })], side.kind);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`ra-side${photo ? ' has-photo' : ''}`}>
      <span className="ra-label">{side.label}{photo && <Check size={14} aria-label="done" />}</span>
      <span className="ra-hint">{side.hint}</span>
      {photo
        ? (
          <span className="ra-photo">
            {photo.url ? <img src={photo.url} alt={`${what}, ${side.label}`} /> : <span className="ra-saved">Saved</span>}
            {canTake && !removing && <button type="button" className="ra-remove" aria-label={`Remove the ${what.toLowerCase()} photo, ${side.label}`} onClick={() => setRemoving(true)}><X size={18} aria-hidden="true" /></button>}
            {removing && (
              <span className="ra-confirm" role="alertdialog" aria-label="Remove this photo?">
                <strong>Remove this photo?</strong>
                <span className="ra-confirm-row">
                  <button type="button" className="btn danger-btn" onClick={remove}>Remove</button>
                  <button type="button" className="btn" onClick={() => setRemoving(false)}>Keep</button>
                </span>
              </span>
            )}
          </span>
        )
        : <span className="ra-empty"><Camera size={26} aria-hidden="true" /></span>}
      {canTake && (
        <div className="ra-buttons">
          <PhotoButtons onFiles={take} disabled={busy} primary={!photo} takeLabel={busy ? 'Adding…' : photo ? 'Retake' : 'Take photo'} title={`${what}, ${side.label}`} />
        </div>
      )}
      {photo?.pending && <span className="small muted">Waiting to send</span>}
      {error && <span className="popup-hint">{error}</span>}
    </div>
  );
}

// One meter photo at close off: see it, or remove it (the wrong one was taken).
function MeterPhoto({ job, photo, n }) {
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState(null);
  const remove = async () => {
    setRemoving(false);
    try { await removePhoto(outbox, job.id, photo); } catch (err) { setError(err.message); }
  };
  return (
    <div className="ra-side has-photo">
      <span className="ra-label">Meter {n}<Check size={14} aria-label="done" /></span>
      <span className="ra-photo">
        {photo.url ? <img src={photo.url} alt={`Meter photo ${n}`} /> : <span className="ra-saved">Saved</span>}
        {!removing && <button type="button" className="ra-remove" aria-label={`Remove meter photo ${n}`} onClick={() => setRemoving(true)}><X size={18} aria-hidden="true" /></button>}
        {removing && (
          <span className="ra-confirm" role="alertdialog" aria-label="Remove this photo?">
            <strong>Remove this photo?</strong>
            <span className="ra-confirm-row">
              <button type="button" className="btn danger-btn" onClick={remove}>Remove</button>
              <button type="button" className="btn" onClick={() => setRemoving(false)}>Keep</button>
            </span>
          </span>
        )}
      </span>
      {photo.pending && <span className="small muted">Waiting to send</span>}
      {error && <span className="popup-hint">{error}</span>}
    </div>
  );
}

// One labelled line of the job's details.
function Fact({ icon, label, tone, children }) {
  return (
    <div className={`fact${tone ? ` fact-${tone}` : ''}`}>
      <dt>{icon} {label}</dt>
      <dd>{children}</dd>
    </div>
  );
}
